import { useState } from "react"
import { Stage, StatePill } from "./Stage"
import { SubStageRow, BlockerAlert } from "./StagePrimitives"
import { Phone } from "lucide-react"
import { APCMState } from "../../types/apcm-ops.types"
import type { ProgramStatus } from "../../types/outreach-program.types"
import * as api from "../../api/apcm-ops.api"
import { ManualRunAction } from "../program/ManualRunAction"

interface Props {
  orgId: string
  stateCounts: Record<string, number>
  stuckScheduledCount: number
  /** Patients still in ENROLLMENT_CONFIRMED / CONSENT_NEEDED who already have a call queued. */
  alreadyScheduled: number
  onMarkScheduled: () => Promise<void>
  onViewPatients: (states: string[]) => void
  /** The clinic program (null when not loaded); a disabled program makes "Start N" a manual run. */
  program: ProgramStatus | null
  onRefresh: () => void
}

export function ConsentStage({
  orgId,
  stateCounts,
  stuckScheduledCount,
  alreadyScheduled,
  onMarkScheduled,
  onViewPatients,
  program,
  onRefresh,
}: Props) {
  const [marking, setMarking] = useState(false)

  const needed = stateCounts[APCMState.CONSENT_NEEDED] || 0
  const scheduled = stateCounts[APCMState.CONSENT_SCHEDULED] || 0
  const obtained = stateCounts[APCMState.CONSENT_OBTAINED] || 0
  const refused = stateCounts[APCMState.CONSENT_REFUSED] || 0
  const maxRetries = stateCounts[APCMState.CONSENT_MAX_RETRIES_REACHED] || 0

  const total = needed + scheduled + obtained
  const offRamp = refused + maxRetries

  return (
    <Stage
      number={2}
      accent="consent"
      icon={Phone}
      title="Consent"
      description="Call patients to get APCM consent. Multiple attempts allowed per patient."
      total={total}
    >
      <ManualRunAction
        noun="consent"
        eligible={needed}
        readyLabel="ready for consent outreach"
        emptyLabel="no patients need consent outreach"
        helpText="Patients in CONSENT_NEEDED. Runs the clinic program for this many patients now: the same texts, cadence, call window and caps as the automatic tick. Max-retries patients are not included; restart them from the Clinic program tab."
        program={program}
        run={(limit) => api.initiateUnconsented(orgId, limit)}
        onDone={onRefresh}
      />

      {alreadyScheduled > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-2">
          <SubStageRow
            label="already have a consent call queued but are not marked CONSENT_SCHEDULED"
            count={alreadyScheduled}
            tone="warning"
            helpText="outcomes only record from CONSENT_SCHEDULED"
            actionLabel="Mark scheduled"
            loading={marking}
            onAction={async () => {
              setMarking(true)
              try {
                await onMarkScheduled()
              } finally {
                setMarking(false)
              }
            }}
          />
        </div>
      )}

      {(scheduled > 0 || obtained > 0) && (
        <div className="rounded-xl border bg-muted/20 px-4 py-2">
          {scheduled > 0 && (
            <SubStageRow
              label="scheduled — awaiting call outcome"
              count={scheduled}
              tone={stuckScheduledCount > 0 ? "warning" : "running"}
              helpText={
                stuckScheduledCount > 0
                  ? `⚠ ${stuckScheduledCount} stuck > 24h — check scheduler`
                  : undefined
              }
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CONSENT_SCHEDULED])}
            />
          )}
          {obtained > 0 && (
            <SubStageRow
              label="consented — ready for care plan"
              count={obtained}
              tone="success"
              helpText="Moves to next stage"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CONSENT_OBTAINED])}
            />
          )}
        </div>
      )}

      {offRamp > 0 && (
        <BlockerAlert
          severity="warning"
          title={`${offRamp} patients off-ramped from consent`}
          description={
            `${refused} refused · ${maxRetries} max-retries reached. These will not receive more consent calls. ` +
            `If any should be re-enrolled, open their patient drawer and click Re-enroll.`
          }
          actionLabel="View off-ramp"
          onAction={() =>
            onViewPatients([
              APCMState.CONSENT_REFUSED,
              APCMState.CONSENT_MAX_RETRIES_REACHED,
            ])
          }
        />
      )}

      <div className="flex flex-wrap gap-1.5">
        <StatePill label="CONSENT_NEEDED" count={needed} tone="amber" />
        <StatePill label="CONSENT_SCHEDULED" count={scheduled} tone="blue" />
        <StatePill label="CONSENT_OBTAINED" count={obtained} tone="emerald" />
        {refused > 0 && <StatePill label="CONSENT_REFUSED" count={refused} tone="red" />}
        {maxRetries > 0 && (
          <StatePill label="CONSENT_MAX_RETRIES_REACHED" count={maxRetries} tone="red" />
        )}
      </div>
    </Stage>
  )
}
